<template>
  <div class="homepage-container" data-ruid="title-dashboard">
    <page-intro-header
      v-if="introHeaderResource"
      :button="introHeaderResource.button"
      :description="introHeaderResource.description"
      :horizontal-layout="introHeaderResource.layout === 'horizontal'"
      :image="introHeaderResource.image"
      :mirror-layout="introHeaderResource.mirrored"
      :title="introHeaderResource.title"
      :video="introHeaderResource.video"
      class="header-container"
    />

    <campaign-widget />

    <challenges-carousel-widget />

    <latest-updates-widget />

    <div class="activity-feed-grid">
      <div class="main-content">
        <latest-tickets-widget
          v-if="widgetVisibility.latestTickets"
          :organization="organization"
          :context-id="contextId"
          :current-user-id="currentUserId"
          :product-id="productId"
          :user-actions="userActions"
        />
      </div>

      <div class="side-content">
        <popular-topics-widget
          v-if="widgetVisibility.popularTopics"
          :product-id="productId"
        />
        <popular-tickets-widget
          v-if="widgetVisibility.popularTickets"
          :organization="organization"
          :context-id="contextId"
          :current-user-id="currentUserId"
          :product-id="productId"
          :user-actions="userActions"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import type {
  IIntroHeader,
  IOrganization,
  IResource,
  IWorkflowV2
} from '@rready/sdk';
import { useViewSettings } from '@rready/composables';
import PageIntroHeader from '../components/pageHeaders/PageIntroHeader.vue';
import LatestUpdatesWidget from '../components/dashboard/LatestUpdatesWidget.vue';
import CampaignWidget from '../module/campaign/CampaignWidget.vue';
import ChallengesCarouselWidget from '../module/gamification/ChallengesCarouselWidget.vue';
import { useContextStore } from '../store/contextStore';
import { useProductStore } from '../store/productStore';
import { useAuthStore } from '../store/authStore';

interface IDashboardWidgetConfig {
  latestTickets: boolean;
  popularTopics: boolean;
  popularTickets: boolean;
}

export default defineComponent({
  name: 'PortalDashboard',
  components: {
    CampaignWidget,
    PageIntroHeader,
    ChallengesCarouselWidget,
    LatestUpdatesWidget
  },
  computed: {
    contextId(): string {
      return useContextStore().contextId!;
    },
    productId(): string {
      return useProductStore().productId!;
    },
    currentUserId(): string {
      return useAuthStore().user!.id;
    },
    organization(): IOrganization {
      return useContextStore().organization;
    },
    workflow(): IWorkflowV2 {
      return useProductStore().workflow;
    },
    widgetVisibility(): IDashboardWidgetConfig {
      const pageSettings = useViewSettings(this.workflow, 'app').page(
        'Dashboard'
      );
      return {
        latestTickets: pageSettings?.latestTickets?.enabled ?? true,
        popularTopics: pageSettings?.popularTopics?.enabled ?? true,
        popularTickets: pageSettings?.popularTickets?.enabled ?? true
      };
    },
    introHeaderResource(): IIntroHeader | null {
      const customization = this.organization.productCustomization;
      const specific = customization?.[this.productId];
      const dashboard = specific?.dashboardOverview?.resources;
      if (dashboard) {
        return this.$res(dashboard as IResource<any>, {}).introHeader;
      }
      return null;
    },
    userActions(): string[] {
      return useAuthStore().permission?.allActions ?? [];
    }
  }
});
</script>

<style lang="scss" scoped>
.homepage-container {
  display: flex;
  flex-direction: column;
  gap: 30px;
  padding-top: 30px;

  .header-container {
    background-image: radial-gradient(
      ellipse 44% 136px at 50% bottom,
      #e5e5e5 -100%,
      #ffffff 100%
    );

    &.page-intro-header {
      padding: 20px 0 50px;
    }
  }
}

.activity-feed-grid {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 32px;

  .main-content {
    flex: 1 1 auto;
    min-width: 500px;
  }

  .side-content {
    flex: 1 0 320px;
    max-width: 320px;
    display: flex;
    flex-direction: column;
    gap: 30px;
  }
}
</style>
